import {TodayPageBox} from '@/components/toto/TodayLayoutContext';
import { UserRound, UsersRound, ClipboardCheck } from "lucide-react";
import { CurtainGlyph } from "@/components/toto/StaffDayViews";
import { useVesselNavigation } from "@/components/toto/VesselNavigation";
import TodayArrival from "@/components/toto/TodayArrival";
import OpenButton from "@/components/toto/OpenButton";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import TotoNavigationGlyph from "@/components/TotoNavigationGlyph";
import { TotoAdaptiveDock } from "@/components/toto/TotoAdaptiveSurface";
import FrontLineHome from "@/pages/FrontLineHome";
import TotoPresence from "@/components/toto/TotoPresence";
import GoogleReviewsWorkspace from "@/components/toto/GoogleReviewsWorkspace";
import EGPerformanceWorkspace from "@/components/toto/EGPerformanceWorkspace";
import PayrollWorkspace from "@/components/toto/PayrollWorkspace";
import TotoClayStudy from "@/pages/TotoClayStudy";
import TotoLayeredSlabStudy from "@/pages/TotoLayeredSlabStudy";
import PayrollVesselStudy from "@/pages/PayrollVesselStudy";
import "@/pages/toto-motion.css";
import "@/components/toto/VesselIdentity.css";
import "@/components/toto/VesselContinuityV2.css";
import "@/components/toto/PayrollWorkspaceOperational.css";
import "@/pages/toto-home-refinement.css";
import "@/pages/toto-environment-glass.css";
import "@/components/toto/TotoGlassIdentity.css";
// Unified Home rules must load last so legacy field styles cannot wash out
// readable labels or reintroduce the dark-field typography treatment.
import "@/pages/toto-unified-surface.css";
import "@/pages/toto-home-dock.css";
import "@/pages/toto-journey-home.css";
import "@/components/toto/today-dashboard.css";

const TEAM_IDENTITIES = {
  plantation: { name: "Team Green", rgb: "92 134 112", hex: "#5c8670", fieldRgb: "231 240 234", fieldGlowRgb: "247 242 231", glassRgb: "14 87 56", glassDeepRgb: "7 48 31", glassHighlightRgb: "37 126 87" },
  fortLauderdale: { name: "Team Blue", rgb: "83 119 155", hex: "#53779b", fieldRgb: "229 237 244", fieldGlowRgb: "244 240 233", glassRgb: "15 64 107", glassDeepRgb: "8 37 66", glassHighlightRgb: "42 108 168" },
  wynwood: { name: "Team Pink", rgb: "176 91 124", hex: "#b05b7c", fieldRgb: "245 232 238", fieldGlowRgb: "250 241 235", glassRgb: "173 48 101", glassDeepRgb: "103 24 59", glassHighlightRgb: "220 92 144" },
  pinecrest: { name: "Team Orange", rgb: "190 111 54", hex: "#be6f36", fieldRgb: "246 235 224", fieldGlowRgb: "252 244 232", glassRgb: "184 88 27", glassDeepRgb: "105 47 12", glassHighlightRgb: "229 132 61" },
  rino: { name: "Team Yellow", rgb: "194 155 49", hex: "#c29b31", fieldRgb: "247 240 213", fieldGlowRgb: "252 247 229", glassRgb: "198 151 19", glassDeepRgb: "116 84 7", glassHighlightRgb: "238 193 55" },
  cherryCreek: { name: "Team Red", rgb: "170 63 63", hex: "#aa3f3f", fieldRgb: "244 228 228", fieldGlowRgb: "250 239 233", glassRgb: "169 43 43", glassDeepRgb: "96 20 20", glassHighlightRgb: "214 77 70" },
};

const RAINBOW_IDENTITY = { name: "Rainbow", rgb: "116 112 124", hex: "#74707c", fieldRgb: "237 234 240", fieldGlowRgb: "246 241 233", glassRgb: "88 80 101", glassDeepRgb: "48 43 58", glassHighlightRgb: "132 120 148" };

function greeting() { const hour = new Date().getHours(); if (hour < 12) return "Good morning"; if (hour < 17) return "Good afternoon"; return "Good evening"; }
function identityFor(user) {
  const raw = [user?.boutique_code, user?.boutique_name, user?.boutique].filter(Boolean).join(" ").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  if (/\bplt\b|plantation/.test(raw)) return TEAM_IDENTITIES.plantation;
  if (/\bfll\b|fort lauderdale/.test(raw)) return TEAM_IDENTITIES.fortLauderdale;
  if (/\bwyn\b|wynwood/.test(raw)) return TEAM_IDENTITIES.wynwood;
  if (/\bpnc\b|pinecrest/.test(raw)) return TEAM_IDENTITIES.pinecrest;
  if (/\brno\b|\brino\b|river north/.test(raw)) return TEAM_IDENTITIES.rino;
  if (/\bcc\b|\bcck\b|cherry creek/.test(raw)) return TEAM_IDENTITIES.cherryCreek;
  return RAINBOW_IDENTITY;
}

function TotoTerrierMark() {
  return <img className="toto-terrier-mark" src="/brand/toto-canonical/original-gold.png" alt="" aria-hidden="true"/>;
}

const MANAGEMENT_VESSELS = [
  {
    id: "payroll",
    title: "Payroll preparation",
    detail: "Evidence, review & release",
    state: "ready",
    stateLabel: "Available",
    destination: "/payroll/prepare",
  },
  {
    id: "scheduling",
    title: "Scheduling",
    detail: "EG schedule & coverage",
    state: "ready",
    stateLabel: "Available",
    destination: "/scheduling/eg",
  },
  {
    id: "team",
    title: "Team",
    detail: "Availability & requests",
    state: "quiet",
    stateLabel: "Quiet",
    destination: "/scheduling/time-off",
  },
  {
    id: "guest-care",
    title: "Guest care",
    detail: "Reviews & recovery",
    state: "quiet",
    stateLabel: "Quiet",
    workspace: "reviews",
  },
  {
    id: "follow-through",
    title: "Follow-through",
    detail: "Invoices & membership exceptions",
    state: "waiting",
    stateLabel: "Not connected",
  },
];

function VesselGlyph({ vessel }) {
  if (vessel.id === "payroll") return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="3.5" width="16" height="17" rx="3"/><path d="M8 8h8M8 12h5M8 16h3"/><path d="m15 16 1.5 1.5L20 14"/></svg>;
  if (vessel.id === "scheduling") return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="5.5" width="17" height="15" rx="3"/><path d="M7.5 3.5v4M16.5 3.5v4M3.5 10h17M8 14h.01M12 14h.01M16 14h.01M8 17h.01M12 17h.01"/></svg>;
  if (vessel.id === "team") return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2.25"/><path d="M3.8 20c.5-3.7 2.5-5.6 5.2-5.6s4.7 1.9 5.2 5.6M14.8 15.3c3.1-.4 5.1 1.1 5.5 4.7"/></svg>;
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 18.5h16M5.5 18.5v-2.2a6.5 6.5 0 0 1 13 0v2.2M8.2 9.2a4.3 4.3 0 0 1 7.6 0"/><circle cx="12" cy="5.3" r="1.15"/></svg>;
}

function VesselDock({ onEnter, enteringVessel, phase }) {
  const available = phase === "settled";
  return <nav className={`toto-home-dock-vessels is-${phase}`} aria-label="Your available vessels" aria-hidden={!available}>
    {MANAGEMENT_VESSELS.filter((vessel) => vessel.state !== "waiting").map((vessel) => <button
      type="button"
      key={vessel.id}
      className={`toto-home-dock-vessel is-${vessel.state} ${enteringVessel === vessel.id ? "is-entering" : ""}`}
      onClick={() => onEnter(vessel)}
      tabIndex={available ? 0 : -1}
      disabled={!available}
      aria-label={`Open ${vessel.title}: ${vessel.detail}`}
    >
      <span className="toto-home-dock-lens" aria-hidden="true"><VesselGlyph vessel={vessel}/></span>
      <span className="toto-home-dock-caption" aria-hidden="true">{vessel.title}</span>
      <span className="toto-home-sr-only">{vessel.title}</span>
    </button>)}
  </nav>;
}

function journeyForRole(role) {
  if (role === "massage_therapist") return { roleLabel: "Massage therapist", headline: "Your workday, held with care.", detail: "Your schedule, room, and daily rhythm live here when they are ready.", workspace: { detail: "Your schedule", destination: "/my-schedule" } };
  if (role === "front_desk") return { roleLabel: "Experience guide", headline: "A clear view of the day ahead.", detail: "The work that needs your attention is gathered here without the noise.", workspace: { detail: "Daily practice", destination: "/daily" } };
  return { roleLabel: "General Manager", headline: "Today’s journey, in view.", detail: "The essential rhythm of your boutique is ready when you are.", workspace: { detail: "Payroll preparation", destination: "/payroll/prepare" } };
}

function JourneyHome() {
  const {key:navigationEntry}=useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { document.title = "TOTO"; }, []);
  return <TodayPageBox as="main" className="journey-home" data-testid="toto-home">
    <TodayPageBox as="section" className="journey-home__arrival" aria-labelledby="journey-heading">
      <TodayArrival user={user} api={api} navigate={navigate} navigationEntry={navigationEntry} />
    </TodayPageBox>
    <TotoAdaptiveDock active="today" />
  </TodayPageBox>;
}

function ManagementHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Navigation is an available spatial field, not a hidden interaction behind
  // a legacy arrival animation. The three role-aware destinations are visible
  // as soon as Today’s Journey opens.
  const [fieldOpen,setFieldOpen]=useState(true);
  const [fieldPhase,setFieldPhase]=useState("settled");
  const [activeWorkspace,setActiveWorkspace]=useState(null);
  const [enteringVessel,setEnteringVessel]=useState(null);
  const phaseTimer=useRef(null);
  const identity=useMemo(()=>identityFor(user),[user]);
  const firstName=user?.name?.split(" ")?.[0]||"there";
  useEffect(()=>()=>window.clearTimeout(phaseTimer.current),[]);
  const releaseToto=()=>{
    window.clearTimeout(phaseTimer.current);
    setFieldOpen(true);
    setFieldPhase("depress");
    phaseTimer.current=window.setTimeout(()=>{
      setFieldPhase("stretch");
      phaseTimer.current=window.setTimeout(()=>{
        setFieldPhase("rise");
        phaseTimer.current=window.setTimeout(()=>{
          setFieldPhase("settled");
        },720);
      },760);
    },480);
  };
  const containToto=()=>{
    window.clearTimeout(phaseTimer.current);
    setActiveWorkspace(null);
    setEnteringVessel(null);
    setFieldPhase("returning");
    phaseTimer.current=window.setTimeout(()=>{setFieldOpen(false);setFieldPhase("contained");},1280);
  };
  const enterVessel=(vessel)=>{
    if(vessel.state === "waiting"||fieldPhase!=="settled") return;
    setEnteringVessel(vessel.id);
    window.setTimeout(()=>{
      if(vessel.workspace) setActiveWorkspace(vessel.workspace);
      if(vessel.destination) navigate(vessel.destination);
    }, 280);
  };
  return <main className={`toto-home toto-field-study ${fieldOpen?"is-field-open":"is-contained"} is-vessel-${fieldPhase} ${activeWorkspace?"has-workspace-open":""}`} style={{"--team-rgb":identity.rgb,"--team-field-rgb":identity.fieldRgb,"--team-field-glow-rgb":identity.fieldGlowRgb,"--team-glass-rgb":identity.glassRgb,"--team-glass-deep-rgb":identity.glassDeepRgb,"--team-glass-highlight-rgb":identity.glassHighlightRgb}} data-testid="toto-home">
    <div className="toto-back-glass" aria-hidden="true"><span className="toto-back-glass-bloom toto-back-glass-bloom-one"/><span className="toto-back-glass-bloom toto-back-glass-bloom-two"/><span className="toto-back-glass-grain"/></div>
    <div className="toto-liquid-field" aria-hidden={!fieldOpen}><div className="toto-liquid-refraction"/><div className="toto-liquid-frost"/></div>
    <header className="toto-field-header">
      <div className="toto-team-mark"><span className="toto-team-dot" aria-hidden="true"/>{identity.name}</div>
      <button
        type="button"
        className="toto-origin-mark"
        onClick={()=>navigate("/sonmi/atrium")}
        aria-label="Open SONMI's original vision"
        title="SONMI's original vision"
      >
        SONMI
      </button>
    </header>
    <section className="toto-field-arrival" aria-labelledby="toto-greeting"><div className="toto-presence-slot">
      <button type="button" className="toto-contained-artifact" onClick={()=>fieldOpen ? containToto() : releaseToto()} aria-label={fieldOpen ? "Return the vessels to Toto" : "Release Toto into the field"} aria-expanded={fieldOpen} data-testid="toto-presence"><TotoPresence teamColor={identity.hex} engaged={fieldOpen} shellVariant="smooth"/></button>
    </div><div className="toto-arrival-copy toto-greeting-card"><div className="toto-home-greeting"><h1 id="toto-greeting">{greeting()}, {firstName}.</h1></div>{fieldOpen&&<VesselDock onEnter={enterVessel} enteringVessel={enteringVessel} phase={fieldPhase}/>}<p className="toto-narration">Everything is here when you need it.</p></div></section>
    {activeWorkspace === "reviews" && <GoogleReviewsWorkspace open onOpen={()=>setActiveWorkspace("reviews")} onClose={()=>setActiveWorkspace(null)}/>}
    <div className="toto-empty-field-content" aria-hidden={!fieldOpen}/>
  </main>;
}

export default function TotoHome(){
  const{user}=useAuth();
  const params=new URLSearchParams(window.location.search);
  const clayStudy=params.get("totoClay")==="1";
  const slabStudy=params.get("totoSlab")==="1";
  const payrollVesselStudy=params.get("payrollVessel")==="1";
  if(payrollVesselStudy)return <PayrollVesselStudy/>;
  if(clayStudy)return <TotoClayStudy/>;
  if(slabStudy){const identity=identityFor(user);return <TotoLayeredSlabStudy teamRgb={identity.rgb} teamName={identity.name}/>;}
  return <JourneyHome/>;
}
